"use client";

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useTranslations } from "next-intl";

type PopupType = "success" | "error" | "warning" | "info";

interface PopupItem {
  id: number;
  type: PopupType;
  title?: string;
  message: string;
  isConfirm?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
}

interface PopupContextValue {
  success: (message: string, onClose?: () => void) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
  confirm: (message: string, onConfirm: () => void, onCancel?: () => void) => void;
}

const PopupContext = createContext<PopupContextValue | null>(null);

let nextId = 0;

export function usePopup(): PopupContextValue {
  const ctx = useContext(PopupContext);
  if (!ctx) throw new Error("usePopup must be used within PopupProvider");
  return ctx;
}

const ICON_BG: Record<PopupType, string> = {
  success: "bg-green-100",
  error: "bg-red-100",
  warning: "bg-amber-100",
  info: "bg-blue-100",
};

function PopupDialog({ item, onClose }: { item: PopupItem; onClose: () => void }) {
  const t = useTranslations('common');

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      style={{ pointerEvents: 'auto' }}
      role="dialog"
      aria-modal="true"
      onClick={!item.isConfirm ? onClose : undefined}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-modal-pop-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${ICON_BG[item.type]}`}
        >
          {item.type === "success" ? (
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : item.type === "warning" ? (
            <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : item.type === "error" ? (
            <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
        {item.title && (
          <h3 className="text-xl font-bold text-dark mb-2 text-center">{item.title}</h3>
        )}
        <p className="text-gray-700 whitespace-pre-wrap text-center mb-6 text-sm leading-relaxed">
          {item.message}
        </p>
        <div className="flex justify-center gap-3">
          {item.isConfirm ? (
            <>
              <button
                type="button"
                onClick={() => {
                  item.onCancel?.();
                  onClose();
                }}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium min-w-[120px]"
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  item.onConfirm?.();
                  onClose();
                }}
                className="px-6 py-2.5 bg-honda-red text-white rounded-lg hover:bg-honda-red-dark transition-colors font-medium min-w-[120px]"
              >
                {t('confirm')}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-2.5 bg-honda-red text-white rounded-lg hover:bg-honda-red-dark transition-colors font-medium min-w-[140px]"
            >
              {t('ok')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function PopupProvider({ children }: { children: React.ReactNode }) {
  const [popups, setPopups] = useState<PopupItem[]>([]);

  const addPopup = useCallback((type: PopupType, message: string, isConfirm = false, onConfirm?: () => void, onCancel?: () => void, onClose?: () => void, title?: string) => {
    const id = nextId++;
    setPopups((prev) => [
      ...prev.filter((p) => p.isConfirm),
      { id, type, title, message, isConfirm, onConfirm, onCancel, onClose },
    ]);
  }, []);

  const removePopup = useCallback((id: number) => {
    setPopups((prev) => {
      const popup = prev.find((p) => p.id === id);
      popup?.onClose?.();
      return prev.filter((p) => p.id !== id);
    });
  }, []);

  const value = useMemo<PopupContextValue>(() => ({
    success: (msg: string, onClose?: () => void) => addPopup("success", msg, false, undefined, undefined, onClose),
    error: (msg: string, title?: string) => addPopup("error", msg, false, undefined, undefined, undefined, title),
    warning: (msg: string, title?: string) => addPopup("warning", msg, false, undefined, undefined, undefined, title),
    info: (msg: string, title?: string) => addPopup("info", msg, false, undefined, undefined, undefined, title),
    confirm: (msg: string, onConfirm: () => void, onCancel?: () => void) => addPopup("warning", msg, true, onConfirm, onCancel),
  }), [addPopup]);

  return (
    <PopupContext.Provider value={value}>
      {children}
      {popups.map((p) => (
        <PopupDialog key={p.id} item={p} onClose={() => removePopup(p.id)} />
      ))}
    </PopupContext.Provider>
  );
}
